import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, User, Mail, Trash2, LogOut, Users, RefreshCw } from "lucide-react";
import { formatLocalDateTime } from "@/utils/formatters";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginHistory = () => {
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadingActive, setLoadingActive] = useState(true);
  const [userRole, setUserRole] = useState('');

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies:", error);
    }
  };

  const fetchActiveSessions = async () => {
    setLoadingActive(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/active-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch active sessions:", error);
    }
    setLoadingActive(false);
  };

  const fetchLoginHistory = async (agencyId = null) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/login-history`;
      if (agencyId && agencyId !== 'all') {
        url += `?agency_id=${agencyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoginHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch login history:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Get user role from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const role = payload.role || '';
        setUserRole(role);
        
        // Fetch data
        fetchLoginHistory();
        if (role === 'admin') {
          fetchAgencies();
          fetchActiveSessions();
        }
      } catch (e) {
        console.error("Failed to decode token:", e);
        fetchLoginHistory();
      }
    } else {
      fetchLoginHistory();
    }
  }, []);

  const handleAgencyFilter = (value) => {
    setSelectedAgency(value);
    fetchLoginHistory(value === 'all' ? null : value);
  };

  const handleClearHistory = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to clear all log history? This will override the 30-day automatic cleanup."
    );
    
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/login-history/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ ${response.data.message}\nDeleted ${response.data.deleted_count} records.`);
      fetchLoginHistory(); // Refresh to show empty list
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to clear history');
    }
  };

  const handleLogoutAllUsers = async () => {
    const confirmed = window.confirm(
      "⚠️ Are you sure you want to log off ALL non-admin users?\n\nThis will immediately invalidate their sessions and they will need to log in again.\n\nAdmin users will NOT be affected."
    );
    
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/logout-all-users`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`✅ ${response.data.message}`);
      // Refresh active sessions list
      fetchActiveSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log off users');
    }
  };

  const formatDateTime = (isoString) => {
    const formatted = formatLocalDateTime(isoString);
    if (formatted === 'N/A') return { date: 'N/A', time: 'N/A' };
    
    // Split the formatted string "DD/MM/YYYY HH:MM" into date and time
    const parts = formatted.split(' ');
    return { 
      date: parts[0] || 'N/A', 
      time: parts[1] || 'N/A' 
    };
  };

  if (loading) {
    return <div style={{ color: '#B0B0B0' }}>Loading login history...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Login History</h1>
        <p className="mt-1" style={{ color: '#B0B0B0' }}>Track all user login activities</p>
        <div className="mt-3 p-3 rounded" style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
          <p className="text-sm" style={{ color: '#60A5FA' }}>
            ℹ️ <strong>Automatic Cleanup:</strong> Login history older than 30 days is automatically deleted to keep storage light.
          </p>
        </div>
      </div>

      {/* Admin Controls */}
      {userRole === 'admin' && (
        <div className="mb-4 flex gap-3 items-center flex-wrap">
          <div style={{ width: '250px' }}>
            <Select value={selectedAgency} onValueChange={handleAgencyFilter}>
              <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                <SelectValue placeholder="Filter by Agency" />
              </SelectTrigger>
              <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                <SelectItem value="all" style={{ color: '#E8E8E8' }}>All Agencies</SelectItem>
                {agencies && agencies.filter(a => a.is_active !== false).map(agency => (
                  <SelectItem key={agency.id} value={agency.id} style={{ color: '#E8E8E8' }}>
                    {agency.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button 
            onClick={handleLogoutAllUsers}
            style={{ 
              background: 'rgba(251, 146, 60, 0.2)', 
              color: '#FB923C',
              border: '1px solid rgba(251, 146, 60, 0.5)'
            }}
            className="hover:bg-orange-900"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Off All Users
          </Button>
          
          <Button 
            onClick={handleClearHistory}
            style={{ 
              background: 'rgba(239, 68, 68, 0.2)', 
              color: '#EF4444',
              border: '1px solid rgba(239, 68, 68, 0.5)'
            }}
            className="hover:bg-red-900"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear History
          </Button>
        </div>
      )}

      {/* Currently Logged In Section - Admin Only */}
      {userRole === 'admin' && (
        <Card className="mb-6" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ color: '#4ade80' }}>
                <Users className="h-5 w-5" />
                Currently Logged In ({activeSessions.length})
              </CardTitle>
              <Button
                onClick={fetchActiveSessions}
                size="sm"
                variant="ghost"
                style={{ color: '#4ade80' }}
                disabled={loadingActive}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingActive ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <p className="text-sm mt-1" style={{ color: '#B0B0B0' }}>
              Users with active sessions (logged in within the last 8 hours)
            </p>
          </CardHeader>
          <CardContent>
            {loadingActive ? (
              <div className="text-center py-4" style={{ color: '#B0B0B0' }}>Loading active sessions...</div>
            ) : activeSessions.length === 0 ? (
              <div className="text-center py-4" style={{ color: '#B0B0B0' }}>No active sessions found</div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {activeSessions.map((session, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ 
                      background: 'rgba(30, 42, 74, 0.8)',
                      border: '1px solid rgba(74, 90, 138, 0.5)'
                    }}
                  >
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ 
                        background: '#4ade80',
                        boxShadow: '0 0 6px #4ade80'
                      }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span style={{ color: '#E8E8E8', fontWeight: '500' }}>
                          {session.user_name || 'Unknown'}
                        </span>
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ 
                            background: session.role === 'admin' ? 'rgba(255, 215, 0, 0.2)' : 
                                       session.role === 'staff' ? 'rgba(96, 165, 250, 0.2)' :
                                       'rgba(192, 192, 192, 0.2)',
                            color: session.role === 'admin' ? '#FFD700' : 
                                  session.role === 'staff' ? '#60A5FA' :
                                  '#C0C0C0'
                          }}
                        >
                          {session.role ? session.role.toUpperCase() : 'N/A'}
                        </span>
                      </div>
                      <div className="text-xs" style={{ color: '#B0B0B0' }}>
                        {session.email}
                      </div>
                      <div className="text-xs" style={{ color: '#6B7280' }}>
                        Last login: {formatDateTime(session.last_login).date} {formatDateTime(session.last_login).time}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
        <CardHeader>
          <CardTitle style={{ color: '#FFD700' }}>
            Recent Logins ({loginHistory ? loginHistory.length : 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <th className="p-3 text-left" style={{ color: '#E8E8E8' }}>Date</th>
                  <th className="p-3 text-left" style={{ color: '#E8E8E8' }}>Time</th>
                  <th className="p-3 text-left" style={{ color: '#E8E8E8' }}>User Name</th>
                  <th className="p-3 text-left" style={{ color: '#E8E8E8' }}>Email</th>
                  <th className="p-3 text-left" style={{ color: '#E8E8E8' }}>Role</th>
                  <th className="p-3 text-left" style={{ color: '#E8E8E8' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {!loginHistory || loginHistory.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-6 text-center" style={{ color: '#B0B0B0' }}>
                      No login history found
                    </td>
                  </tr>
                ) : (
                  loginHistory.map((log, idx) => {
                    const { date, time } = formatDateTime(log.login_time);
                    return (
                      <tr 
                        key={log.id} 
                        style={{ 
                          borderBottom: '1px solid rgba(74, 90, 138, 0.2)',
                          background: idx % 2 === 0 ? 'rgba(10, 17, 40, 0.3)' : 'transparent'
                        }}
                      >
                        <td className="p-3" style={{ color: '#E8E8E8' }}>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" style={{ color: '#FFD700' }} />
                            {date}
                          </div>
                        </td>
                        <td className="p-3" style={{ color: '#B0B0B0' }}>{time}</td>
                        <td className="p-3" style={{ color: '#E8E8E8' }}>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" style={{ color: '#60A5FA' }} />
                            {log.user_name}
                          </div>
                        </td>
                        <td className="p-3" style={{ color: '#B0B0B0' }}>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4" style={{ color: '#B0B0B0' }} />
                            {log.email}
                          </div>
                        </td>
                        <td className="p-3">
                          <span 
                            className="px-2 py-1 rounded text-xs font-semibold"
                            style={{ 
                              background: log.role === 'admin' ? 'rgba(255, 215, 0, 0.2)' : 
                                         log.role === 'staff' ? 'rgba(96, 165, 250, 0.2)' :
                                         'rgba(192, 192, 192, 0.2)',
                              color: log.role === 'admin' ? '#FFD700' : 
                                    log.role === 'staff' ? '#60A5FA' :
                                    '#C0C0C0'
                            }}
                          >
                            {log.role ? log.role.toUpperCase() : 'N/A'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span 
                            className="px-2 py-1 rounded text-xs font-semibold"
                            style={{ 
                              background: 'rgba(34, 197, 94, 0.2)',
                              color: '#4ade80'
                            }}
                          >
                            {log.status ? log.status.toUpperCase() : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginHistory;
